import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { Panel, AsyncPanelBody, EvidenceBadge } from "../components/primitives";
import { ShieldAlert, Send } from "lucide-react";

const TG = `${API}/v1/admin/administration`;
const tg = () => axios.create({ baseURL: TG, headers: { Authorization: `Bearer ${localStorage.getItem("taven_admin_token")}` } });

const useTG = (path, deps = []) => {
  const [s, setS] = useState({ loading: true, data: null, error: null });
  const load = useCallback(() => {
    setS((p) => ({ ...p, loading: true }));
    tg().get(path).then(({ data }) => setS({ loading: false, data: data.data, error: null }))
      .catch((e) => setS({ loading: false, data: null, error: { code: e?.response?.status || "ERROR", message: e?.response?.data?.detail || "Request failed" } }));
  }, [path]);
  useEffect(load, [load, ...deps]);
  return { ...s, refetch: load };
};

export function OfficesPanel() {
  const { loading, data, error, refetch } = useTG("/offices");
  const [sel, setSel] = useState(null);
  if (sel) return <OfficeWorkspace officeId={sel} onBack={() => setSel(null)} />;
  return (
    <Panel testid="cc-panel-tegob-offices" title="TAVEN Departments"
      subtitle={data ? `Canonical v${data.registry_version} · ${data.canonical_department_count} PhD departments · single layer · 24/7` : "PhD operational departments"}>
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        {data && (
          <div data-testid="cc-tegob-drift-banner" className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
            <ShieldAlert className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-amber-200/90">
              Canonical TEGOB = <b>{data.canonical_department_count} PhD-level departments</b> (single layer, no Master layer — the "18 Master + 5 PhD" model is <b>superseded source drift</b>). Engineering runtime is operational (ABAC, audit, evidence, protected fail-closed). <b>OAQA & OMADA are NOT TEGOB departments</b> (independent Twin Center authorities); no department can issue a PASS/GO. <b>23/23 governance PASS is NOT proven</b> (OAQA PENDING).
            </p>
          </div>
        )}
        <p className="text-[10px] uppercase tracking-wider text-[#C5A059] font-bold mb-2" data-testid="cc-tegob-phd-heading">23 PhD Operational Departments · 24/7</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(data?.offices || []).map((o) => (
            <button key={o.office_id} data-testid={`cc-office-${o.office_id}`} onClick={() => setSel(o.office_id)}
              className="text-left bg-[#141a28] border border-white/10 rounded-lg p-4 hover:border-[#C5A059]/50 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C5A059]">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-white">{o.name}</p>
                <span className="text-[9px] uppercase tracking-wider text-[#C5A059] font-bold">{o.code}</span>
              </div>
              <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{o.mission}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3 text-[10px] text-zinc-400">
                <span className="px-1.5 py-0.5 rounded bg-[#C5A059]/15 text-[#C5A059] font-bold">PhD</span>
                <span>Active: <b className="text-white">{o.active_tasks_count.value}</b></span>
                <span>Pending appr: <b className="text-white">{o.pending_approvals_count.value}</b></span>
                <span className="flex items-center gap-1">Health: <EvidenceBadge state={o.health.value} /></span>
              </div>
            </button>
          ))}
        </div>
      </AsyncPanelBody>
    </Panel>
  );
}

export function OfficeWorkspace({ officeId, onBack }) {
  const { loading, data, error, refetch } = useTG(`/offices/${officeId}/workspace`);
  const o = data?.office;
  return (
    <Panel testid={`cc-office-workspace-${officeId}`} title={o?.director_title || "Office Workspace"}
      subtitle={o?.mission}
      actions={<button onClick={onBack} data-testid="cc-office-back" className="text-xs font-bold text-[#C5A059] hover:text-white">← All Offices</button>}>
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        {o && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-[11px]">
            <Chip k="Office ID" v={o.office_id} />
            <Chip k="Authority" v={o.default_autonomy} />
            <Chip k="Active Tasks" v={o.active_tasks_count.value} />
            <Chip k="Pending Approvals" v={o.pending_approvals_count.value} />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 text-xs">
          <div className="bg-[#141a28] border border-white/10 rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Allowed Actions</p>
            <p className="text-zinc-300">{(o?.allowed_actions || []).join(", ") || "—"}</p>
          </div>
          <div className="bg-[#141a28] border border-white/10 rounded-lg p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Prohibited (fail-closed)</p>
            <p className="text-zinc-400">{(o?.prohibited_actions || []).slice(0, 6).join(", ")}…</p>
          </div>
        </div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Assigned Tasks ({data?.tasks?.total ?? 0})</p>
        {(data?.tasks?.items || []).length === 0
          ? <p className="text-xs text-zinc-500">No tasks assigned to this office yet.</p>
          : (data.tasks.items.map((t) => (
              <div key={t.task_id} className="flex items-center justify-between border-b border-white/5 py-2 text-xs">
                <span className="text-white">{t.title}{t.protected_actions?.length ? <span className="ml-1 text-[9px] text-[#F26522] font-bold">PROTECTED</span> : null}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-zinc-200 border border-white/10">{t.state}</span>
              </div>
            )))}
        {o && <DepartmentAssistant officeId={officeId} deptName={o.name || o.director_title} />}
      </AsyncPanelBody>
    </Panel>
  );
}

export function DepartmentAssistant({ officeId, deptName }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [disclosure, setDisclosure] = useState("");
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      const { data } = await tg().post(`/departments/${officeId}/chat`, { message: text, session_id: sid, audience: "internal" });
      const d = data.data;
      setSid(d.session_id);
      setDisclosure(d.disclosure);
      setMsgs((m) => [...m, { role: "assistant", content: d.reply, refused: d.refused, route: d.route_preview }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistant unavailable");
      setMsgs((m) => [...m, { role: "assistant", content: "Assistant unavailable right now.", error: true }]);
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border-t border-white/10 pt-4" data-testid="cc-dept-assistant">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#C5A059] mb-1">Department Assistant</p>
      <p className="text-[11px] text-amber-200/80 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 mb-3" data-testid="cc-dept-assistant-disclosure">
        {disclosure || `You are chatting with a TAVEN Virtual Department Assistant for ${deptName || "this department"}. A human team member may review or take over. Protected actions (release / OAQA PASS / OMADA GO) are refused and escalated.`}
      </p>
      <div className="space-y-2 max-h-72 overflow-y-auto mb-3" data-testid="cc-dept-assistant-log">
        {msgs.length === 0 && <p className="text-xs text-zinc-500">Ask this department assistant a question about its operational scope.</p>}
        {msgs.map((m, i) => (
          <div key={i} data-testid={`cc-dept-msg-${m.role}`}
            className={`text-xs rounded-lg px-3 py-2 ${m.role === "user" ? "bg-[#C5A059]/15 text-white ml-8" : "bg-[#141a28] border border-white/10 text-zinc-200 mr-8"}`}>
            <span className="whitespace-pre-wrap">{m.content}</span>
            {m.refused && <span className="block mt-1 text-[10px] text-[#F8b58c] font-bold">Protected action → refused & routed to human escalation.</span>}
          </div>
        ))}
        {busy && <p className="text-xs text-zinc-500" data-testid="cc-dept-assistant-typing">Assistant is thinking…</p>}
      </div>
      <div className="flex gap-2">
        <input data-testid="cc-dept-assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message the department assistant…"
          className="flex-1 bg-[#141a28] border border-white/10 rounded-md text-xs text-white px-3 py-2 outline-none" />
        <button data-testid="cc-dept-assistant-send" onClick={send} disabled={busy}
          className="inline-flex items-center gap-1.5 bg-[#C5A059] text-black rounded-md text-xs font-bold px-3 py-2 hover:bg-white disabled:opacity-50">
          <Send className="w-3.5 h-3.5" /> Send
        </button>
      </div>
    </div>
  );
}

const Chip = ({ k, v }) => (
  <div className="bg-[#141a28] border border-white/10 rounded-lg px-3 py-2">
    <p className="text-[9px] uppercase tracking-wider text-zinc-500">{k}</p>
    <p className="text-white font-bold mt-0.5 break-all">{v}</p>
  </div>
);

export function OfficeResponsesPanel() {
  const { loading, data, error, refetch } = useTG("/tasks");
  const rows = (data?.items || []).filter((t) => t.latest_response || ["COMPLETED", "WAITING_FOR_APPROVAL"].includes(t.state));
  return (
    <Panel testid="cc-panel-tegob-responses" title="Office Responses"
      subtitle="Office → Task → Status → Latest Response → Approval">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}
        empty={rows.length === 0 && { title: "No office responses yet" }}>
        <ul className="divide-y divide-white/5">
          {rows.map((t) => (
            <li key={t.task_id} data-testid="cc-office-response-row" className="py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-white font-medium">{t.title}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-zinc-200 border border-white/10">{t.state}</span>
              </div>
              <p className="text-[10px] text-zinc-500 mt-0.5">{t.office_id}{t.protected_actions?.length ? " · PROTECTED" : ""}{t.escalation_ids?.length ? " · escalated" : ""}</p>
              {t.latest_response && <p className="text-xs text-zinc-300 mt-1 bg-white/5 border border-white/10 rounded px-2 py-1">{t.latest_response}</p>}
            </li>
          ))}
        </ul>
      </AsyncPanelBody>
    </Panel>
  );
}

const OFFICE_OPTS_UNUSED = null;

export function TasksPanel() {
  const { loading, data, error, refetch } = useTG("/tasks");
  const offices = useTG("/offices");
  const [form, setForm] = useState({ office_id: "OFF-M-ESP-001", title: "", objective: "", protected: false });
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); refetch(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } };
  const create = () => {
    if (!form.title || !form.objective) return toast.error("Title and objective required");
    act(() => tg().post("/tasks", { office_id: form.office_id, title: form.title, objective: form.objective,
      protected_actions: form.protected ? ["PRODUCTION_DEPLOY"] : [] }), "Task created");
    setForm({ ...form, title: "", objective: "" });
  };
  const transition = (t, target) => act(() => tg().post(`/tasks/${t.task_id}/transition`, { target_state: target, expected_version: t.version }), `→ ${target}`);
  const accept = (t) => act(() => tg().post(`/tasks/${t.task_id}/accept`, { expected_version: t.version }), "Accepted");
  const escalate = (t) => act(() => tg().post(`/tasks/${t.task_id}/escalations`, { escalation_type: "PROTECTED_DECISION", reason: "Requires authorization", requested_decision: "approve", expected_version: t.version }), "Escalated");
  const NEXT = { NEW: ["ACCEPTED"], ACCEPTED: ["IN_PROGRESS"], IN_PROGRESS: ["WAITING_FOR_APPROVAL", "COMPLETED"], WAITING_FOR_APPROVAL: ["COMPLETED", "REJECTED"] };
  return (
    <div className="space-y-5">
      <Panel testid="cc-panel-tegob-tasks-create" title="Assign Governed Task">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select data-testid="tegob-task-office" value={form.office_id} onChange={(e) => setForm({ ...form, office_id: e.target.value })}
            className="bg-[#141a28] border border-white/10 rounded-md text-xs text-white px-2 py-2 outline-none">
            {(offices.data?.offices || []).map((o) => <option key={o.office_id} value={o.office_id} className="bg-[#0b0f1a]">{o.director_title}</option>)}
          </select>
          <input data-testid="tegob-task-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="bg-[#141a28] border border-white/10 rounded-md text-xs text-white px-2 py-2 outline-none" />
          <input data-testid="tegob-task-objective" placeholder="Objective" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}
            className="bg-[#141a28] border border-white/10 rounded-md text-xs text-white px-2 py-2 outline-none" />
          <button data-testid="tegob-task-create" onClick={create} className="inline-flex items-center justify-center gap-1.5 bg-[#C5A059] text-black rounded-md text-xs font-bold px-3 py-2 hover:bg-white">
            <Send className="w-3.5 h-3.5" /> Create
          </button>
        </div>
        <label className="flex items-center gap-2 mt-2 text-[11px] text-zinc-400">
          <input type="checkbox" checked={form.protected} onChange={(e) => setForm({ ...form, protected: e.target.checked })} data-testid="tegob-task-protected" />
          <ShieldAlert className="w-3.5 h-3.5 text-[#F26522]" /> Include a protected action (PRODUCTION_DEPLOY) — completion will fail-closed until escalated
        </label>
      </Panel>

      <Panel testid="cc-panel-tegob-tasks" title="Task Management" subtitle="Governed lifecycle — protected actions fail closed">
        <AsyncPanelBody loading={loading} error={error} onRetry={refetch} empty={(data?.items || []).length === 0 && { title: "No tasks yet", message: "Assign a governed task above." }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                <th className="py-2 pr-3">Title</th><th className="py-2 pr-3">Office</th><th className="py-2 pr-3">State</th><th className="py-2 pr-3">Autonomy</th><th className="py-2">Actions</th>
              </tr></thead>
              <tbody>
                {(data?.items || []).map((t) => (
                  <tr key={t.task_id} data-testid={`tegob-task-row`} className="border-b border-white/5">
                    <td className="py-2.5 pr-3 text-white">{t.title}{t.protected_actions?.length ? <span className="ml-1 text-[9px] text-[#F26522] font-bold">PROTECTED</span> : null}</td>
                    <td className="py-2.5 pr-3 text-zinc-400 text-xs">{t.office_id}</td>
                    <td className="py-2.5 pr-3"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-zinc-200 border border-white/10">{t.state}</span></td>
                    <td className="py-2.5 pr-3 text-[10px] text-zinc-400">{t.assigned_autonomy}</td>
                    <td className="py-2.5 flex flex-wrap gap-1">
                      {t.state === "NEW" && <button onClick={() => accept(t)} data-testid="tegob-accept" className="text-[10px] font-bold bg-[#C5A059] text-black px-2 py-1 rounded">Accept</button>}
                      {(NEXT[t.state] || []).map((s) => <button key={s} onClick={() => transition(t, s)} className="text-[10px] font-bold bg-white/10 text-white px-2 py-1 rounded hover:bg-white/20">{s}</button>)}
                      {t.protected_actions?.length ? <button onClick={() => escalate(t)} data-testid="tegob-escalate" className="text-[10px] font-bold bg-[#F26522]/20 text-[#F8b58c] px-2 py-1 rounded">Escalate</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncPanelBody>
      </Panel>
    </div>
  );
}
